import { userFacingMessage } from '../../api/errors'
import { useUpdateProfile, type Profile } from '../../api/queries/profile'
import { SettingsCard, useDraft } from './SettingsCard'
import styles from './Settings.module.css'

/**
 * How long a deal you have marked Stop investing stays on the board.
 *
 * Its own file rather than another card in ProfileCards: that file sits at 243
 * lines against a 250 threshold (AF8), and this is a different responsibility
 * anyway — the only setting on this screen that changes what the Pipeline shows.
 *
 * The windows come from the backend's closed vocabulary, so a fourth option
 * cannot appear here without the server offering it first.
 */

/** null = Never. The wire format's own set; not a hand-written frontend enum. */
type Window = NonNullable<Profile['auto_archive_days']> | null

const OPTIONS: { value: Window; label: string }[] = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: null, label: 'Never' },
]

function describe(days: Window): string {
  if (days === null) {
    return 'Auto-archive is off. Deals leave the board only when you archive them or record an outcome.'
  }
  return `Deals you mark Stop investing are archived ${days} days after you mark them. Nothing is deleted. Find them under Show archived in Pipeline, and restore any of them.`
}

export function AutoArchiveCard({ profile }: { profile: Profile }) {
  const d = useDraft({ days: (profile.auto_archive_days ?? null) as Window })
  const update = useUpdateProfile()

  return (
    <SettingsCard
      title="Auto-archive"
      description="Automatically move deals you have marked Stop investing out of your active pipeline."
      summary={[profile.auto_archive_days ? `After ${profile.auto_archive_days} days` : 'Off']}
      editing={d.editing}
      onEdit={d.start}
      onCancel={d.cancel}
      // Sent as null rather than omitted: null is the stored value for Never, so
      // leaving the key out would mean "unchanged" and the choice would not save.
      onSave={() => update.mutate({ auto_archive_days: d.draft.days }, { onSuccess: d.done })}
      saving={update.isPending}
      error={update.isError ? userFacingMessage(update.error) : null}
    >
      <fieldset className={styles.optionSet}>
        <legend className="dp-sr-only">Archive stopped deals after</legend>
        <div className={styles.checks}>
          {OPTIONS.map((option) => (
            <label key={String(option.value)} className={styles.check}>
              <input
                type="radio"
                name="auto-archive-days"
                value={String(option.value)}
                checked={d.draft.days === option.value}
                onChange={() => d.set('days', option.value)}
                disabled={!d.editing}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
      {/* Describes the draft while editing, so the consequence of the option you
          are about to save is visible before you save it. */}
      <p className={styles.hint}>{describe(d.editing ? d.draft.days : (profile.auto_archive_days ?? null))}</p>
    </SettingsCard>
  )
}
